import { useState, type FormEvent } from 'react';
import {
  PLAN_CELL_MODES,
  PLAN_FIELDS,
  PLAN_FIELD_LABELS,
  WEEKDAY_LABELS,
  cellMode,
  dateOfWeekday,
  stripHtml,
  type PlanCell,
  type PlanCellMode,
  type PlanRow,
  type Weekday,
  type WeekKey,
} from '@td/shared';
import { useSaveCell } from '../hooks/useLessonPlans';
import { RichTextField } from './RichTextField';
import { StandardsField } from './StandardsField';
import { useAuth } from '../auth/AuthContext';
import { Modal } from './Modal';

const PLACEHOLDERS: Record<string, string> = {
  objective: 'Students will be able to…',
  standards: '2.NBT.B.5',
  activities: 'Mini-lesson, partner practice, exit ticket',
  materials: 'Unit 3 packet, base-ten blocks',
  assessment: 'Exit ticket',
  notes: 'Pull small group: Mia, Diego',
};

export function PlanCellEditor({
  week,
  row,
  weekday,
  cell,
  onClose,
}: {
  week: WeekKey;
  row: PlanRow;
  weekday: Weekday;
  cell: PlanCell | undefined;
  onClose: () => void;
}) {
  const save = useSaveCell();
  const { user } = useAuth();
  const [values, setValues] = useState<PlanCell>(cell ?? {});
  const [busy, setBusy] = useState(false);

  /*
   * Simple unless this cell says otherwise.
   *
   * Most days are a line of writing, not a form with six labelled boxes, and
   * the boxes were making every bathroom break look like a lesson study. A
   * cell written before this existed still opens detailed — `cellMode` reads
   * its filled-in fields — so nothing already planned changes shape.
   */
  const [mode, setMode] = useState<PlanCellMode>(() => cellMode(cell));

  const date = dateOfWeekday(week, weekday);

  /** Content on the side she is not looking at, kept and mentioned. */
  const hiddenDetail = PLAN_FIELDS.some((field) => stripHtml(values[field]));
  const hiddenSimple = !!stripHtml(values.plan);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      /*
       * Both sides are sent, whichever one is showing.
       *
       * Switching to simple must not throw away a detailed lesson — she may
       * be glancing at it, or may switch back in a minute. The mode decides
       * what is shown and printed; it does not decide what is kept.
       */
      await save.mutateAsync({ week, rowId: row.id, weekday, values: { ...values, mode } });
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      onClose={onClose}
      label="Lesson plan cell"
      className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-card bg-white p-5 shadow-lifted"
    >
      <div className="mb-4">
        <h2 className="text-lg">{row.label}</h2>
        <p className="text-xs text-stone">
          {WEEKDAY_LABELS[weekday]},{' '}
          {date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}
          {row.timeStart ? ` · ${row.timeStart}${row.timeEnd ? `–${row.timeEnd}` : ''}` : ''}
        </p>
      </div>

      {/*
        Two words, not a settings page. Whether this lesson wants one box or
        six is a decision made while writing it, so it belongs at the top of
        the thing being written.
      */}
      <div className="mb-4 inline-flex rounded-lg border border-sand p-0.5">
        {PLAN_CELL_MODES.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={mode === option}
            onClick={() => setMode(option)}
            className={[
              'rounded-md px-3 py-1 text-sm capitalize transition-colors',
              mode === option
                ? 'bg-terracotta text-cream'
                : 'text-walnut hover:bg-linen hover:text-espresso',
            ].join(' ')}
          >
            {option}
          </button>
        ))}
      </div>

      <form onSubmit={onSubmit} className="space-y-3">
        {mode === 'simple' ? (
          <div>
            <RichTextField
              label="This lesson"
              value={values.plan ?? ''}
              placeholder="What is happening this lesson…"
              minHeight={220}
              onChange={(html) => setValues((v) => ({ ...v, plan: html }))}
            />

            {/*
              Said rather than hidden. Switching to simple over a detailed
              lesson looks like the lesson has been wiped, and the one thing
              worse than losing planning is thinking you have.
            */}
            {hiddenDetail && (
              <p className="mt-1.5 text-xs text-stone">
                The objective, standards and the rest are still here — switch to Detailed to see
                them. Nothing has been deleted.
              </p>
            )}
          </div>
        ) : (
          <>
            {hiddenSimple && (
              <p className="text-xs text-stone">
                What you wrote in Simple is kept too, and comes back if you switch.
              </p>
            )}

            {PLAN_FIELDS.map((field) => (
              <div key={field}>
                <span className="label">{PLAN_FIELD_LABELS[field]}</span>
                {field === 'standards' ? (
                  <StandardsField
                    grade={user?.settings.gradeLevel ?? null}
                    value={values.standards ?? ''}
                    onChange={(html) => setValues((v) => ({ ...v, standards: html }))}
                  />
                ) : (
                  <RichTextField
                    label={PLAN_FIELD_LABELS[field]}
                    value={values[field] ?? ''}
                    placeholder={PLACEHOLDERS[field]}
                    onChange={(html) => setValues((v) => ({ ...v, [field]: html }))}
                  />
                )}
              </div>
            ))}
          </>
        )}

        <div className="flex justify-between gap-2 pt-1">
          <button type="button" onClick={() => setValues({})} className="btn-soft-danger">
            Clear this cell
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
